import { throttle } from '../../utils/throttle'

/**
 * Dealer Locator Mobile Drawer Handle
 */
export class GMXDLDrawerHandle extends HTMLElement {
	dealerLocatorParent: HTMLElement
	searchPanelParent: HTMLElement

	private boundDragStartHandler = throttle(this._handleDragStart.bind(this), 250)
	private boundDragMoveHandler = this._handleDragMove.bind(this)
	private boundDragEndHandler = this._handleDragEnd.bind(this)
	private boundMouseLeaveHandler = this._handleMouseLeave.bind(this)
	private boundWindowResizeHandler = throttle(this._handleWindowResize.bind(this), 250, {
		leading: true,
		trailing: true,
	})

	// Drag/swipe variables
	private isGrabbed = false
	private grabbedY: number = null
	private startTop: number = null

	private SEARCH_PANEL_MIN_HEIGHT = 350
	private DEALER_DETAILS_MIN_HEIGHT = 450

	private _currTopOffset = 0
	get currentTopOffset(): number {
		return this._currTopOffset
	}
	set currentTopOffset(value: number) {
		this._currTopOffset = value
		this.setAttribute('data-curr-top-off', value.toString())
	}

	connectedCallback(): void {
		this.dealerLocatorParent = this.closest('gmx-dealer-locator')
		this.searchPanelParent = this.closest('.search-panel')

		if (this.dealerLocatorParent && this.searchPanelParent) {
			this.addEventListener('mousedown', this.boundDragStartHandler)
			this.addEventListener('touchstart', this.boundDragStartHandler)

			this.dealerLocatorParent.addEventListener('mousemove', this.boundDragMoveHandler)
			this.dealerLocatorParent.addEventListener('touchmove', this.boundDragMoveHandler)

			this.dealerLocatorParent.addEventListener('mouseup', this.boundDragEndHandler)
			this.dealerLocatorParent.addEventListener('touchend', this.boundDragEndHandler)

			this.dealerLocatorParent.addEventListener('mouseleave', this.boundMouseLeaveHandler)

			window.addEventListener('resize', this.boundWindowResizeHandler)
		} else {
			window.console.error(
				'GMXDLDrawerHandle: Could not find required parent elements. Ensure this component is used within a gmx-dealer-locator and has a .search-panel ancestor.'
			)
		}

		this.currentTopOffset = 0
	}

	disconnectedCallback(): void {
		this.removeEventListener('mousedown', this.boundDragStartHandler)
		this.removeEventListener('touchstart', this.boundDragStartHandler)

		this.dealerLocatorParent.removeEventListener('mousemove', this.boundDragMoveHandler)
		this.dealerLocatorParent.removeEventListener('touchmove', this.boundDragMoveHandler)

		this.dealerLocatorParent.removeEventListener('mouseup', this.boundDragEndHandler)
		this.dealerLocatorParent.removeEventListener('touchend', this.boundDragEndHandler)

		window.removeEventListener('resize', this.boundWindowResizeHandler)
	}

	/**
	 * Handles the start of a drag/swipe action, setting initial positions and preventing text selection.
	 * @param e
	 * @private
	 */
	private _handleDragStart(e: MouseEvent | TouchEvent): void {
		this.isGrabbed = true
		this.startTop = this.currentTopOffset
		document.body.style.userSelect = 'none' // Prevent text selection

		if ('touches' in e && e.type === 'touchstart') {
			this.grabbedY = e.touches[0].clientY
		} else {
			if ('clientY' in e) {
				this.grabbedY = e.clientY
			}
		}
	}

	/**
	 * Handles the movement during a drag/swipe action, updating the position of the drawer and adjusting the search panel height accordingly.
	 * @param e
	 * @private
	 */
	private _handleDragMove(e: MouseEvent | TouchEvent): void {
		if (!this.isGrabbed) return

		e.preventDefault()
		e.stopPropagation()

		let currY
		if ('touches' in e && e.type === 'touchmove') {
			currY = e.touches[0].clientY
		} else {
			if ('clientY' in e) {
				currY = e.clientY
			}
		}

		let newTopPos = this.startTop - (this.grabbedY - currY)

		if (this.dealerLocatorParent.offsetHeight - newTopPos <= this.SEARCH_PANEL_MIN_HEIGHT) {
			newTopPos = this.dealerLocatorParent.offsetHeight - this.SEARCH_PANEL_MIN_HEIGHT
		}

		if (newTopPos <= 0) {
			this.searchPanelParent.style.height = ''
			this.currentTopOffset = 0
		} else if (this.searchPanelParent.offsetHeight >= this.SEARCH_PANEL_MIN_HEIGHT) {
			this.searchPanelParent.style.height = `calc(100% - ${newTopPos}px)`
			this.currentTopOffset = newTopPos
		}
	}

	/**
	 * Handles the end of a drag/swipe action, resetting the grabbed state and re-enabling text selection.
	 * @private
	 */
	private _handleDragEnd(): void {
		this.isGrabbed = false
		document.body.style.userSelect = '' // Re-enable text selection
	}

	private _handleMouseLeave(): void {
		if (this.isGrabbed) {
			this._handleDragEnd()
		}
	}

	/**
	 * Anchors the drawer to the maximum height allowed by the dealer locator container, ensuring the search panel does not shrink below its minimum height.
	 */
	anchorToMaxHeight(): void {
		const maxTop = this.dealerLocatorParent.offsetHeight - this.SEARCH_PANEL_MIN_HEIGHT
		this.searchPanelParent.style.height = `calc(100% - ${maxTop}px)`
		this.currentTopOffset = maxTop
	}

	unsetHeight(): void {
		this.searchPanelParent.style.height = ''
		this.currentTopOffset = 0
	}

	/**
	 * Expand the search panel to at least the dealer-details minimum height.  No-op when the panel
	 * is already tall enough, or when the locator itself is too short to accommodate the target.
	 */
	ensureDetailsMinHeight(): void {
		if (!this.dealerLocatorParent || !this.searchPanelParent) return

		const parentHeight = this.dealerLocatorParent.offsetHeight
		const currentHeight = this.searchPanelParent.offsetHeight
		const targetHeight = Math.min(this.DEALER_DETAILS_MIN_HEIGHT, parentHeight)

		if (currentHeight < targetHeight && targetHeight > 0) {
			const newTop = parentHeight - targetHeight
			this.searchPanelParent.style.height = `calc(100% - ${newTop}px)`
			this.currentTopOffset = newTop
		}
	}

	_handleWindowResize(): void {
		if (this.searchPanelParent.classList.contains('has-results')) {
			if (!window.matchMedia('screen and (min-width: 481px)').matches) {
				if (!this.currentTopOffset) {
					this.anchorToMaxHeight()
				}
			} else if (this.currentTopOffset) {
				this.unsetHeight()
			}
		}
	}
}
if (!customElements.get('gmx-dl-drawer-handle')) {
	customElements.define('gmx-dl-drawer-handle', GMXDLDrawerHandle)
}
